import React, { Fragment, Suspense } from 'react';
import { lazyComponent } from '@unisporkal/react-lazy-component';
import { ButtonVariantEnum } from '@unisporkal/alliance-ui';
import acquisitionActions from '../../support/acquisitionActions.ts';
import MobileStickyButton from './MobileStickyButton/MobileStickyButton';
import styles from './AcquisitionButtons.module.scss';
import useAvailableAcquisitionActions from '../../hooks/useAvailableAcquisitionActions.ts';

const buttonMap = {
  [acquisitionActions.BUY_ULTRA_PACK]: {
    component: lazyComponent(() =>
      import('./UltraPackBuyNowButton/UltraPackBuyNowButton')
    ),
  },
  [acquisitionActions.CART]: {
    component: lazyComponent(() => import('./CartButtons/CartButton')),
  },
  [acquisitionActions.CONTACT_US_MARKET_FREEZE]: {
    component: lazyComponent(() =>
      import('./MarketFreezeContactUsButton/MarketFreezeContactUsButton')
    ),
  },
  [acquisitionActions.DOWNLOAD]: {
    component: lazyComponent(() => import('./DownloadButton/DownloadButton')),
  },
  [acquisitionActions.MODIFIED_DOWNLOAD]: {
    component: lazyComponent(() =>
      import('./ModifiedDownloadButton/ModifiedDownloadButton')
    ),
  },
  [acquisitionActions.MODIFIED_DOWNLOAD_COMPLETED]: {
    component: lazyComponent(() =>
      import('./ModifiedRedownloadButton/ModifiedRedownloadButton')
    ),
  },
  [acquisitionActions.BATCH_DOWNLOAD]: {
    component: lazyComponent(() =>
      import('./BatchDownloadButton/BatchDownloadButton')
    ),
  },
  [acquisitionActions.REQUEST_PREVIEW]: {
    component: lazyComponent(() =>
      import('./RequestPreviewButton/RequestPreviewButton')
    ),
  },
  [acquisitionActions.RETURN_TO_ROYALTY_FREE]: {
    component: lazyComponent(() =>
      import('./ReturnToRoyaltyFreeButton/ReturnToRoyaltyFreeButton')
    ),
  },
  [acquisitionActions.SAVE_TO_CART]: {
    component: lazyComponent(() => import('./CartButtons/CartButton')),
  },
  [acquisitionActions.SELECT_OPTIONS]: {
    component: lazyComponent(() =>
      import('./SelectOptionsButton/SelectOptionsButton')
    ),
  },
  [acquisitionActions.VIEW_PRICING_OPTIONS]: {
    component: lazyComponent(() =>
      import('./ViewPricingOptionsButton/ViewPricingOptionsButton')
    ),
  },
  [acquisitionActions.VIEW_ULTRA_PACK_UPSELL]: {
    component: lazyComponent(() =>
      import('./UltraPackUpsellButton/UltraPackUpsellButton')
    ),
  },
  [acquisitionActions.CHAT_SIGN_IN]: {
    component: lazyComponent(() => import('./SignOnButton/SignOnButton')),
  },
  [acquisitionActions.CHAT_WITH_US]: {
    component: lazyComponent(() =>
      import('./ChatWithUsButton/ChatWithUsButton')
    ),
  },
  [acquisitionActions.PROCEED_TO_CHECKOUT]: {
    component: lazyComponent(() =>
      import('./ProceedToCheckoutButton/ProceedToCheckoutButton')
    ),
  },
  [acquisitionActions.VIEW_CART]: {
    component: lazyComponent(() => import('./CartButtons/ViewCartButton')),
  },
  [acquisitionActions.GET_WITH_ULTRAPACK]: {
    component: lazyComponent(() =>
      import('./GetWithUltrapackButton/GetWithUltrapackButton')
    ),
  },
  [acquisitionActions.MODIFIED_LICENSE_REDOWNLOAD]: {
    component: lazyComponent(() =>
      import('./LicensedRedownloadButton/LicensedRedownloadButton')
    ),
  },
  [acquisitionActions.MODIFIED_CART_CONFIRMATION]: {
    component: lazyComponent(() =>
      import('./ModifiedDownloadButton/ModifiedDownloadButton')
    ),
  },
  [acquisitionActions.RETURN_TO_MODIFIED_ROYALTY_FREE]: {
    component: lazyComponent(() =>
      import(
        './ReturnToModifiedRoyaltyFreeButton/ReturnToModifiedRoyaltyFreeButton'
      )
    ),
  },
  [acquisitionActions.SUBSCRIPTION_DOWNLOAD]: {
    component: lazyComponent(() => import('./DownloadButton/DownloadButton')),
  },
  [acquisitionActions.SUBSCRIPTION_SUBSCRIBE]: {
    component: lazyComponent(() =>
      import('./SubscriptionSubscribeButton/SubscriptionSubscribeButton')
    ),
  },
  [acquisitionActions.SUBSCRIPTION_ADD_TO_CART]: {
    component: lazyComponent(() => import('./CartButtons/CartButton')),
  },
  [acquisitionActions.SUBSCRIPTION_BUY_NOW]: {
    component: lazyComponent(() => import('./AlaCarteBuyNow/AlaCarteBuyNow')),
  },
  [acquisitionActions.SUBSCRIPTION_LINKS]: {
    component: lazyComponent(() =>
      import(
        '../../getty/logicalComponents/SubscriptionLinks/SubscriptionLinks'
      )
    ),
  },
};

const AcquisitionButtons = () => {
  const { response: availableAcquisitionActions } =
    useAvailableAcquisitionActions();

  const buildButtons = () =>
    availableAcquisitionActions?.map((action, index) => {
      const Button = buttonMap[action]?.component;

      const buttonVariant =
        index === 0 ? ButtonVariantEnum.PRIMARY : ButtonVariantEnum.HOLLOW;
      // First CTA is always the primary CTA and MobileStickyButton for mobile screen sizes
      if (index === 0 && Button) {
        return (
          <Fragment key={action}>
            <div className={styles.regularButton}>
              <Button
                btnClassNames={styles.buyCardButton}
                buttonVariant={buttonVariant}
              />
            </div>
            <MobileStickyButton action={action}>
              <Button
                btnClassNames={styles.buyCardButton}
                buttonVariant={buttonVariant}
              />
            </MobileStickyButton>
          </Fragment>
        );
      }
      return (
        Button && (
          <div
            className={styles.regularButton}
            key={action}
          >
            <Button
              btnClassNames={styles.buyCardButton}
              buttonVariant={buttonVariant}
            />
          </div>
        )
      );
    });

  return (
    <div
      data-testid="acquisition-buttons-container"
      className={styles.buttonContainer}
    >
      <Suspense>{buildButtons()}</Suspense>
    </div>
  );
};

export default AcquisitionButtons;
